import { ICON__shuffle_solid } from '@/assets/icons_fa';
import NotifierBorder from '@/components/ui/notifier/notifier-border';
import { UI_PRIMITIVE__button_with_icon_animated } from '@/components/ui/UI_PRIMITIVE__button_with_icon_animated';
import { UI_PRIMITIVE__tooltip } from '@/components/ui/UI_PRIMITIVE__tooltip';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import i18n_page_generation from '@/locales/keys/i18n_page_generation.json';
import { PLACEHOLDER_BUTTON__bar_10_5 } from '@/pages/_base/buttons/PLACEHOLDER_BUTTON__bar_10_5';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_equal } from '@shared/asserts/utils_asserts';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD__list_token } from '@shared/dto/generation/graph_invoke/_fields/_primitives/list/FIELD__list_token';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PROPS {
  field_list: FIELD__list_token;
}

export function G_BUTTON__prompt_randomize_tokens_weights({ field_list }: PROPS) {
  /*
   * STORE
   */
  const { t } = useTranslation(ENUM__i18n_namespace.PAGE_GENERATION);

  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  const { generation_current } = use_slice_generation(['generation_current']);

  const component_id = `${ENUM__component_type.PAGE_GENERATION_BUTTON_RANDOMIZE_TOKENS_WEIGHTS}_${generation_current._id}`;
  const notifier = HOOK__notifier(component_id, ENUM__workspace_entity.ENTITY_GENERATION);

  const tooltip_title = t(i18n_page_generation.sidebar_user_input.button.randomize_tokens_weights.tooltip);

  const [is_loading, set_is_loading] = useState(false);

  /*
   * EVENTS
   */

  async function onClick(e: React.MouseEvent) {
    e.stopPropagation();

    assert_equal(generation_current.tool.graph.meta.graph_id, field_list.meta.graph_id);

    let field_list_new = field_list.clone();

    let tokens = field_list_new.items_visible;

    for (let token of tokens) {
      let weight_new = token.weight.get_value_range().get_random_value(1);

      token.weight.set_value(weight_new);
    }

    /*
     *
     */

    set_is_loading(true);

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_list_new);

    await notifier.send_action();

    set_is_loading(false);
  }

  function is_disabled(): boolean {
    return g_general__ui_disabled || field_list.items_visible.is_empty() || is_loading;
  }

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_BUTTON__bar_10_5 />;

  return (
    <UI_PRIMITIVE__tooltip {...{ tooltip_title }}>
      <UI_PRIMITIVE__button_with_icon_animated
        variant="no-border-on-bar"
        size="rect-10-5"
        iconClassNameBase="!text-component-item-foreground !size-3"
        iconClassNameHovered="!text-bar-foreground"
        renderDelay={CONSTANTS_TIME__client.TIMEOUT_DELAY_BEFORE_FIRST_ANIMATION_RENDER}
        Icon={ICON__shuffle_solid}
        disabled={is_disabled()}
        {...{ onClick }}
      >
        <NotifierBorder {...{ notifier }} />
      </UI_PRIMITIVE__button_with_icon_animated>
    </UI_PRIMITIVE__tooltip>
  );
}
